<template>
  <div class="collection-company-container">
    <!-- ページヘッダー -->
    <div class="page-header mb-4">
      <div class="header-content">
        <h4 class="mb-0">
          <i class="bi bi-truck me-2 text-primary"></i>収集企業設定
        </h4>
        <BaseButton
          variant="secondary"
          size="sm"
          @click="$emit('back-to-menu')"
        >
          <i class="bi bi-arrow-left me-2"></i>メニューに戻る
        </BaseButton>
      </div>
    </div>

    <!-- データテーブル -->
    <InfoTableSection
      title=""
      :headers="['企業名', '企業色', '操作']"
      :items="companies"
      :loading="loading"
      :error="error"
      empty-message="登録されている企業がありません"
      :pagination="{ enabled: true, perPage: 10 }"
      @action="(payload) => emit('table-action', payload)"
    >
      <template #tableRow="{ item }">
        <BTd class="company-name">
          <ContentText color="default">{{ item.companyName }}</ContentText>
        </BTd>
        <BTd class="text-center">
          <div class="color-display">
            <div
              class="color-preview-small"
              :style="{ backgroundColor: item.companyColor }"
            ></div>
            <span class="color-name-small">{{ item.companyColorName }}</span>
          </div>
        </BTd>
        <BTd class="text-center">
          <BaseButton
            size="sm"
            variant="success"
            class="me-2"
            @click="$emit('show-edit-modal', item)"
          >
            <i class="bi bi-pencil-square me-1"></i>編集
          </BaseButton>
          <BaseButton
            size="sm"
            variant="danger"
            @click="$emit('confirm-delete', item)"
          >
            <i class="bi bi-trash me-1"></i>削除
          </BaseButton>
        </BTd>
      </template>

    </InfoTableSection>

    <!-- 新規追加ボタン -->
    <div class="mt-4">
      <BaseButton
        variant="primary"
        size="lg"
        @click="$emit('show-add-modal')"
      >
        <i class="bi bi-plus-circle me-2"></i>新規追加
      </BaseButton>
    </div>

    <!-- 新規追加・編集モーダル -->
    <BaseModal
      modal-id="collection-company-form-modal"
      :model-value="showModal"
      @update:model-value="$emit('close-modal')"
      :title="isEditMode ? '収集企業編集' : '収集企業新規追加'"
      size="lg"
      @confirm="emit('submit')"
      @cancel="$emit('close-modal')"
      :confirm-text="isEditMode ? '更新' : '追加'"
    >
      <CollectionCompanyForm
        :model-value="formData"
        :errors="formErrors"
        :company-order="companyOrder"
        @update:model-value="(val) => emit('update:form-data', val)"
      />
    </BaseModal>

    <!-- 削除確認モーダル -->
    <BaseModal
      modal-id="delete-collection-company-modal"
      :model-value="showDeleteModal"
      @update:model-value="$emit('close-delete-modal')"
      title="削除確認"
      variant="danger"
      confirm-text="削除"
      @confirm="$emit('delete-company', deleteTarget)"
      @cancel="$emit('close-delete-modal')"
    >
      <p class="mb-3">
        <strong>{{ deleteTarget?.companyName }}</strong> を削除してもよろしいですか？
      </p>
      <div class="alert alert-warning">
        <i class="bi bi-exclamation-triangle me-2"></i>
        この操作は元に戻すことができません。
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { BTd } from 'bootstrap-vue-next'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import ContentText from '@/components/atoms/ContentText.vue'
import InfoTableSection from '@/components/organisms/InfoTableSection.vue'
import CollectionCompanyForm from './CollectionCompanyForm.vue'

const props = defineProps({
  companies: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  showModal: {
    type: Boolean,
    default: false
  },
  showDeleteModal: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({ companyName: '' })
  },
  formErrors: {
    type: Object,
    default: () => ({})
  },
  deleteTarget: {
    type: Object,
    default: null
  },
  companyOrder: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits([
  'show-add-modal',
  'show-edit-modal',
  'confirm-delete',
  'add-company',
  'edit-company',
  'delete-company',
  'close-modal',
  'close-delete-modal',
  'back-to-menu',
  'table-action',
  'submit',
  'update:form-data'
])
</script>

<style lang="scss" scoped>
.collection-company-container {
  padding: 1.5rem 0;
}

.page-header {
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 2rem;
    margin-right: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;
  }

  h4 {
    color: #1f2937;
    font-weight: 600;
    flex: 1;
    display: flex;
    align-items: center;
    margin-bottom: 0;
  }
}

.company-name {
  font-weight: 600;
  color: #374151;
}

/* テーブル下の新規追加ボタンエリア */
.mt-4 {
  margin-left: 2rem !important;
  margin-right: 2rem;
}

.color-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.color-preview-small {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.color-name-small {
  font-size: 0.875rem;
  color: #495057;
}

/* ボタンアニメーション */
.btn-outline-primary:hover,
.btn-outline-danger:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.btn-outline-primary:hover {
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-outline-danger:hover {
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

// 戻るボタンのスタイリング
.page-header button {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

@media (max-width: 768px) {
  .page-header {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
    }

    h4 {
      order: 1; // モバイルではタイトルを上に
    }

    button {
      order: 2; // ボタンを下に
      align-self: flex-end; // 右寄せ維持
    }
  }

  .mt-4 {
    margin-left: 1rem !important;
    margin-right: 1rem;
  }

  .color-display {
    flex-direction: column;
    gap: 0.25rem;
  }
}
</style>
